import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UnsafeBehaviorCache } from 'src/lib/utilities/cache';
import { WorkQueueEntityModel } from '../../api/work-queue/work-queue-entity.model';
import {
	WorkQueueModel,
	WorkQueueTypes,
} from '../../api/work-queue/work-queue.model';
import { WorkQueueService } from '../../api/work-queue/work-queue.service';

@Injectable({
	providedIn: 'root',
})
export class WorkQueueStoreService {
	private workQueueCache = new UnsafeBehaviorCache<
		[queueOwnerUid: number, queueType: WorkQueueTypes],
		WorkQueueModel[]
	>(
		([queueOwnerUid, queueType]) =>
			this.workQueueService.getQueues(queueOwnerUid, queueType),
		`WorkQueueStoreService QueueCache`,
	);

	private _queueEntitiesCache = new UnsafeBehaviorCache<
		[
			queueOwnerUid: number,
			queueId: number,
			queueType: WorkQueueTypes,
			studentId: number | null,
		],
		WorkQueueEntityModel[]
	>(
		([queueOwnerUid, queueId, queueType, studentId]) =>
			this.workQueueService.getQueueEntities(
				queueOwnerUid,
				queueId,
				queueType,
				studentId,
			),
		'WorkQueueStoreService QueueEntitiesCache',
	);

	constructor(private workQueueService: WorkQueueService) {}

	/**
	 * Work Queues
	 */
	public workQueues$ = (
		queueOwnerUid: number,
		type: WorkQueueTypes,
	): Observable<WorkQueueModel[]> => {
		return this.workQueueCache.getCache([queueOwnerUid, type]);
	};

	public refreshQueues = (
		queueOwnerUid: number,
		queueType: WorkQueueTypes,
	): Promise<boolean> => {
		return this.workQueueCache.fetchData([queueOwnerUid, queueType], true);
	};

	/**
	 * Queue Entities
	 */

	public workQueueEntities$ = (
		queueOwnerUid: number,
		queueId: number,
		queueType: WorkQueueTypes,
		studentId: number | null = null,
	): Observable<WorkQueueEntityModel[]> => {
		return this._queueEntitiesCache.getCache([
			queueOwnerUid,
			queueId,
			queueType,
			studentId,
		]);
	};

	public refreshEntities = (
		queueOwnerUid: number,
		queueId: number,
		queueType: WorkQueueTypes,
	): Promise<boolean> => {
		const promises: Promise<boolean>[] = [];

		this._queueEntitiesCache.getStoredKeys().forEach((key) => {
			if (
				key[0] === queueOwnerUid &&
				key[1] === queueId &&
				key[2] === queueType
			) {
				promises.push(this._queueEntitiesCache.fetchData(key, true));
			}
		});

		return Promise.all(promises).then((results) =>
			results.every((result) => result),
		);
	};
}
